/**
 * DDD Domain Integration Module
 * 
 * This module provides the integration interface for DDD Domain
 * following Eric Evans Strategic DDD patterns. It exposes the
 * Published Language contracts that downstream contexts can depend on.
 * 
 * Integration Patterns:
 * - Published Language: Stable contracts for downstream contexts
 * - Customer/Supplier: DDD is upstream, consumers are downstream  
 * - Anti-Corruption Layer: Consumers should translate to their own models
 */

// Export Published Language contracts
export * from './contracts';

/**
 * DDD Domain - Published Language
 * 
 * This is the canonical interface that external bounded contexts
 * should use when integrating with the DDD Domain.
 * 
 * Eric Evans Pattern: Customer/Supplier with Published Language
 * - DDD Domain = Supplier (upstream)
 * - AI Domain, Builder MCP, etc. = Customers (downstream)
 */
export const publishedLanguage = {
  /**
   * Domain information - Runtime metadata
   */
  domain: {
    name: 'DDD Domain',
    version: '1.0.0',
    description: 'Domain-Driven Design strategic and tactical patterns implementation'
  },

  /**
   * Integration Capabilities - What this domain provides to consumers
   */
  capabilities: {
    // Strategic Design
    'create-domain': 'Create new business domains with clear boundaries',
    'create-bounded-context': 'Define bounded contexts within domains',
    'map-context-relationships': 'Define relationships between bounded contexts',
    
    // Tactical Design
    'create-aggregate': 'Create aggregates as consistency boundaries',
    'define-domain-events': 'Define and publish domain events',
    'create-value-objects': 'Create immutable value objects',
    
    // Package Analysis
    'analyze-package-mapping': 'Analyze package-to-domain alignment',
    'validate-architecture': 'Validate DDD architectural compliance',
    'generate-context-map': 'Generate comprehensive context mapping'
  },

  /**
   * Domain Events - Events published for downstream consumption
   */
  events: {
    // Domain Events
    'domain-created': 'New business domain established',
    'bounded-context-created': 'New bounded context defined',
    'aggregate-created': 'New aggregate established',
    
    // Architecture Events
    'package-mapped': 'Package mapped to domain boundary',
    'architecture-validated': 'DDD architecture compliance verified',
    'context-map-updated': 'Context mapping relationships updated'
  },

  /**
   * Business Rules - Key constraints and policies
   */
  businessRules: {
    'domain-uniqueness': 'Domain names must be unique within organization',
    'bounded-context-autonomy': 'Bounded contexts must be autonomous and independently deployable',
    'aggregate-consistency': 'Aggregates ensure consistency within their boundaries',
    'ubiquitous-language': 'Use consistent language within bounded context boundaries'
  },

  /**
   * Integration Patterns - Recommended integration approaches
   */
  integrationPatterns: {
    'published-language': 'Use Published Language contracts for integration',
    'anti-corruption-layer': 'Implement ACL when integrating with external systems',
    'event-driven': 'Use domain events for loose coupling',
    'shared-kernel': 'Share common concepts through well-defined interfaces'
  }
};

/**
 * Integration metadata for downstream contexts
 */
export const DDD_INTEGRATION_INFO = {
  version: '1.0.0',
  context: 'DDD Domain',
  role: 'Upstream Supplier',
  relationships: [
    'AI Domain (Customer)',
    'Builder MCP (Customer)',
    'Designer MCP (Customer)'
  ],
  publishedLanguage: {
    contracts: [
      'DomainQueryCriteria',
      'BoundedContextQueryCriteria',
      'AggregateQueryCriteria',
      'DomainSummary',
      'BoundedContextSummary', 
      'AggregateSummary',
      'DDDStatistics',
      'DDDQueryResult'
    ],
    stability: 'Stable - Breaking changes will increment major version'
  }
} as const;